import React, { useRef, useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import CountUp from "react-countup";
import useOnScreen from "./use-on-screen";
import { SafeHtmlParser } from "./SafeHtmlParser";

/**
 * SingleStat:
 *  - If number <= 2, skip CountUp and just display the number.
 *  - If > 2, CountUp from 0 → number once "startCounting" is true.
 *  - heading is appended right after the numeric portion in the same <h3>.
 *  - body is shown in a paragraph below the heading.
 */
function SingleStat({
	number,
	heading,
	body,
	colour,
	duration,
	startCounting,
}) {
	const skipCountUp = number <= 2;

	let displayContent;
	if (skipCountUp) {
		// Just show the number
		displayContent = number;
	} else {
		// Show CountUp only if we've started
		displayContent = startCounting ? (
			<CountUp
				start={0}
				end={number}
				duration={duration || 3}
				useEasing={false}
				decimals={number.toString().includes(".") ? 2 : 0}
				separator=""
			/>
		) : (
			0
		);
	}

	return (
		<Col xs={12} md={4} style={{ minHeight: "100%" }}>
			<div
				style={{ borderLeft: `4px solid ${colour}` }}
				className="text-start h-100 ps-4 py-2 text-lg-start"
			>
				<div className="d-flex align-items-center">
					<h3 className=" display-4">{displayContent}</h3>

					<SafeHtmlParser htmlContent={heading} />
				</div>
				{body && <p className="pb-0 mb-0 outfit-bold">{body}</p>}
			</div>
		</Col>
	);
}

/**
 * PssStats:
 *  - Observes the entire section.
 *  - Once visible, sets "startCounting=true" so all stats begin together.
 *
 * `stats` array example:
 *  [
 *    { number: 98, heading: "% satisfaction", body: "Description...", colour: "#fede34", duration: 2 },
 *    { number: 2, heading: " awards", body: "Description...", colour: "#562a79" },
 *    ...
 *  ]
 */
const PssStats = ({ stats = [] }) => {
	const containerRef = useRef(null);
	const isVisible = useOnScreen(containerRef, "-50px");
	const [startCounting, setStartCounting] = useState(false);

	useEffect(() => {
		if (isVisible) {
			setStartCounting(true);
		}
	}, [isVisible]);

	return (
		<section className="py-5 py-lg-7" ref={containerRef}>
			<Container className="h-100">
				<Row className="g-4 h-100 g-lg-6">
					{stats.map((stat, index) => (
						<SingleStat
							key={index}
							number={stat.number}
							heading={stat.heading}
							body={stat.body}
							colour={stat.colour}
							duration={stat.duration}
							startCounting={startCounting}
						/>
					))}
				</Row>
			</Container>
		</section>
	);
};

export default PssStats;
